import type { SolverInput, SolverOutput } from '../model/types';
import { solveCone } from './solveCone';
import type { SolverRequest } from './solver.worker';

interface Pending {
  resolve: (output: SolverOutput) => void;
  reject: (err: unknown) => void;
}

let worker: Worker | null | undefined;
let nextId = 0;
const pending = new Map<number, Pending>();

function getWorker(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    worker = new Worker(new URL('./solver.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<{ id: number; output: SolverOutput }>) => {
      pending.get(e.data.id)?.resolve(e.data.output);
      pending.delete(e.data.id);
    };
    worker.onerror = (e) => {
      for (const p of pending.values()) p.reject(e.error ?? new Error(e.message));
      pending.clear();
    };
  } catch (err) {
    console.warn('Web Worker unavailable, solving on main thread', err);
    worker = null;
  }
  return worker;
}

/** Runs the solver off the main thread when possible. */
export function solve(input: SolverInput): Promise<SolverOutput> {
  const w = getWorker();
  if (!w) return Promise.resolve().then(() => solveCone(input));
  return new Promise((resolve, reject) => {
    const id = nextId++;
    pending.set(id, { resolve, reject });
    w.postMessage({ id, input } satisfies SolverRequest);
  });
}
